import clsx from 'clsx';
import type { CSSProperties } from 'react';
import type { ReaderTag } from '@/api/client';
import { Badge } from '@/components/ui/Badge/Badge';
import './readerCatalogTagPill.css';

const TAG_HEX6_RE = /^#[0-9A-Fa-f]{6}$/i;
const TAG_HEX3_RE = /^#[0-9A-Fa-f]{3}$/i;

function normalizedReaderTagHex(color: string): string {
  const t = color.trim();
  if (TAG_HEX6_RE.test(t)) return t.toLowerCase();
  if (TAG_HEX3_RE.test(t)) {
    const r = t[1]!;
    const g = t[2]!;
    const b = t[3]!;
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return '#42a5f5';
}

function readerCatalogTagBadgeBackground(color: string): string {
  const h = normalizedReaderTagHex(color);
  return `linear-gradient(135deg, ${h}, color-mix(in srgb, ${h} 68%, #0f0f0f))`;
}

function accentVarStyle(color: string): CSSProperties {
  return { ['--reader-catalog-tag-accent' as string]: normalizedReaderTagHex(color) } as CSSProperties;
}

function ReaderCatalogTagBadge({ tag }: { tag: Pick<ReaderTag, 'name' | 'color'> }) {
  const bg = readerCatalogTagBadgeBackground(tag.color);
  return (
    <Badge variant="custom" bg={bg} color="#fff">
      <span className="reader-catalog-tag__label">{tag.name}</span>
    </Badge>
  );
}

export function ReaderCatalogTagPill({
  tag,
  className,
  style,
}: {
  tag: Pick<ReaderTag, 'name' | 'color'>;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <span
      className={clsx('reader-catalog-tag', className)}
      title={tag.name}
      style={{ ...accentVarStyle(tag.color), ...style }}
    >
      <ReaderCatalogTagBadge tag={tag} />
    </span>
  );
}

/** Число + краткий суффикс в визуале тега справочника (`reader-catalog-tag--comfortable`). */
export function ReaderCatalogTagMetricPill({
  value,
  suffix,
  color = '#42a5f5',
}: {
  value: string;
  suffix: string;
  /** HEX акцента градиента (как `ReaderTag.color`) */
  color?: string;
}) {
  const bg = readerCatalogTagBadgeBackground(color);
  const title = `${value} ${suffix}`.trim();
  return (
    <span className="reader-catalog-tag reader-catalog-tag--comfortable" title={title} style={accentVarStyle(color)}>
      <Badge variant="custom" bg={bg} color="#fff">
        <span className="reader-catalog-tag__label reader-catalog-tag-metric-pill__label">
          <span className="reader-catalog-tag-metric-pill__val">{value}</span>
          <span className="reader-catalog-tag-metric-pill__suffix">{suffix}</span>
        </span>
      </Badge>
    </span>
  );
}

export function ReaderCatalogTagPillWithRemove({
  tag,
  busy,
  onRequestEdit,
  onRequestRemove,
  className,
}: {
  tag: ReaderTag;
  busy: boolean;
  onRequestEdit?: () => void;
  onRequestRemove: () => void;
  className?: string;
}) {
  const badge = <ReaderCatalogTagBadge tag={tag} />;

  const body =
    onRequestEdit != null ? (
      <button
        type="button"
        className="reader-catalog-tag__hit"
        title={tag.name}
        aria-label={`Редактировать тег «${tag.name}»`}
        disabled={busy}
        onClick={onRequestEdit}
      >
        {badge}
      </button>
    ) : (
      badge
    );

  return (
    <div
      className={clsx('reader-catalog-tag reader-catalog-tag--with-action', className)}
      style={accentVarStyle(tag.color)}
    >
      {body}
      <button
        type="button"
        className="reader-catalog-tag__remove"
        aria-label={`Удалить тег «${tag.name}»`}
        disabled={busy}
        onClick={onRequestRemove}
      >
        ×
      </button>
    </div>
  );
}
